import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useMintTotal from '../util/use-mint-total';

/**
 * The site header from the eyes/ landing pages: wordmark, centre menu, MINT
 * CTA, supply pill, and the burger + panel that replace them on small screens.
 *
 * Markup is the original's. `home` picks which set of links the document used:
 * index.html points its centre menu and drawer at bare `#section` anchors,
 * every other page reaches them through `/#section`.
 *
 * Styling comes from whichever stylesheet the host page loads — eyes.css,
 * watch.css or the chrome-only chrome.css.
 */
export default function EyesNav({ home = false, onMint }) {
  const navRef = useRef(null);
  const minted = useMintTotal();

  const sect = (name) => (home ? `#${name}` : `/#${name}`);

  // A page that mints in place passes onMint; the CTA then stays put instead
  // of routing to /mint. Which element this is, is fixed at mount, so the
  // mobile panel's close-on-click handler still finds it.
  const mintCta = (className) =>
    onMint ? (
      <a className={className} href="/mint" onClick={onMint}>
        MINT NOW
      </a>
    ) : (
      <Link className={className} to="/mint">
        MINT NOW
      </Link>
    );

  useEffect(() => {
    const cleanups = [];

    // Header glass after leaving the very top.
    const nav = navRef.current;
    if (nav) {
      const setNav = () =>
        nav.classList.toggle('nav-scrolled', window.scrollY > 55);
      setNav();
      window.addEventListener('scroll', setNav, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', setNav));
    }

    // ===== mobile-burger-menu-script =====
    document.querySelectorAll('.mobile-burger').forEach((btn) => {
      const panel = btn.nextElementSibling;
      if (!panel || !panel.classList.contains('mobile-menu-panel')) return;

      const closeMenu = () => {
        btn.classList.remove('open');
        panel.classList.remove('open');
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-label', 'Open menu');
        panel.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      };
      const onToggle = () => {
        const opening = !panel.classList.contains('open');
        if (opening) {
          btn.classList.add('open');
          panel.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
          btn.setAttribute('aria-label', 'Close menu');
          panel.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        } else closeMenu();
      };
      const onKeyDown = (e) => {
        if (e.key === 'Escape') closeMenu();
      };

      const links = [...panel.querySelectorAll('a')];
      btn.addEventListener('click', onToggle);
      links.forEach((a) => a.addEventListener('click', closeMenu));
      document.addEventListener('keydown', onKeyDown);

      cleanups.push(() => {
        btn.removeEventListener('click', onToggle);
        links.forEach((a) => a.removeEventListener('click', closeMenu));
        document.removeEventListener('keydown', onKeyDown);
        document.body.style.overflow = '';
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <>
      <nav ref={navRef}>
        <div className="wrap nav">
          <a className="logo real-logo" href={home ? '#' : '/'} aria-label="The Eyes Are Always Watching">
            <img src="/eyes/assets/776ab65b02.png" alt="The Eyes Are Always Watching" />
          </a>
          <div className="center-nav">
            <a href={sect('about')}>
              ABOUT
            </a>
            <a href={sect('lore')}>
              LORE
            </a>
            <a href={sect('bounty')}>
              BOUNTY
            </a>
            <a href={sect('gallery')}>
              GALLERY
            </a>
            <a href="/watch">
              WATCH
            </a>
            <a href={sect('faq')}>
              FAQ
            </a>
          </div>
          {mintCta('btn r')}
          <span className="countpill">
            {minted === null ? '…' : minted} / 21,000
          </span>
        </div>
        {' '}
        <button className="mobile-burger" type="button" aria-label="Open menu" aria-expanded="false">
          {' '}
          <span>
          </span>
          <span>
          </span>
          <span>
          </span>
          {' '}
        </button>
        {' '}
        <div className="mobile-menu-panel" aria-hidden="true">
          {' '}
          <a href="/#about">
            ABOUT
          </a>
          {' '}
          <a href="/#lore">
            LORE
          </a>
          {' '}
          <a href="/#bounty">
            BOUNTY
          </a>
          {' '}
          <a href="/#gallery">
            GALLERY
          </a>
          {' '}
          <a href="/watch">
            WATCH
          </a>
          {' '}
          <a href="/#faq">
            FAQ
          </a>
          {' '}
          {mintCta('mobile-mint')}
          {' '}
          <div className="mobile-menu-socials">
            {' '}
            <a href="https://www.x.com/theeyesbtc" target="_blank" rel="noopener" aria-label="X / Twitter">
              {' '}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.244 2H21.5l-7.115 8.13L22.75 22h-6.55l-5.13-6.708L5.2 22H1.94l7.6-8.687L1.5 2h6.716l4.638 6.13L18.244 2Zm-1.143 17.91h1.804L7.236 3.98H5.3L17.101 19.91Z" />
              </svg>
              {' '}
            </a>
            {' '}
            <a href="#" aria-label="Discord">
              {' '}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19.54 5.34A16.7 16.7 0 0 0 15.44 4l-.5 1.02a15.4 15.4 0 0 0-5.87 0L8.55 4a16.9 16.9 0 0 0-4.1 1.35C1.85 9.2 1.15 12.95 1.5 16.65A16.5 16.5 0 0 0 6.53 19.2l1.22-1.67a10.7 10.7 0 0 1-1.92-.93l.47-.36c3.7 1.72 7.72 1.72 11.38 0l.48.36c-.62.37-1.27.68-1.93.93l1.22 1.67a16.4 16.4 0 0 0 5.04-2.55c.42-4.29-.72-8-2.95-11.31ZM8.7 14.38c-1.12 0-2.03-1.03-2.03-2.29 0-1.27.89-2.3 2.03-2.3 1.15 0 2.05 1.04 2.03 2.3 0 1.26-.9 2.29-2.03 2.29Zm6.6 0c-1.12 0-2.03-1.03-2.03-2.29 0-1.27.89-2.3 2.03-2.3 1.15 0 2.05 1.04 2.03 2.3 0 1.26-.88 2.29-2.03 2.29Z" />
              </svg>
              {' '}
            </a>
            {' '}
          </div>
          {' '}
        </div>
        {' '}
      </nav>
      <div className="mobile-menu-drawer" aria-label="Mobile navigation">
        <div className="mobile-menu-drawer-inner">
          <a href={sect('about')}>
            ABOUT
          </a>
          <a href={sect('lore')}>
            LORE
          </a>
          <a href={sect('bounty')}>
            BOUNTY
          </a>
          <a href={sect('gallery')}>
            GALLERY
          </a>
          <a href="/watch">
            WATCH
          </a>
          <a href={sect('faq')}>
            FAQ
          </a>
          {mintCta('mobile-mint')}
        </div>
      </div>
    </>
  );
}
